import { useCallback, useEffect, useState } from "react";
import { TIMING } from "../constants/timingConstants";
import {
    getListById,
    createList,
    createMenu,
    createPage,
    createMenuItem,
    createWebsiteStructure,
    createAsset,
    deleteListItem,
    updateListItem,
    setPublishStatus,
    setPublishStatusForItems,
} from "../Api/CurationApi";
import { CurationItem } from "../Api/types";
import { AddContentOptions, EditContentOptions } from "../types";

/**
 * Normalize published value to boolean
 * @param value
 * @returns {boolean}
 */
const normalizePublished = (value: unknown): boolean => {
    if (typeof value === "boolean") {
        return value;
    }
    if (typeof value === "string") {
        return value.toLowerCase() === "true";
    }
    if (typeof value === "number") {
        return value === 1;
    }
    return false;
};

/**
 * Normalize curation item
 * @param item
 * @returns {CurationItem}
 */
const normalizeCurationItem = (item: CurationItem): CurationItem => {
    const normalizedChildren = item.children?.map((child) => normalizeCurationItem(child));
    return {
        ...item,
        published: normalizePublished(item.published),
        children: normalizedChildren,
    };
};

/**
 * Extract publisher ID from item metadata
 * @param item
 * @returns {string | null}
 */
/**
 * Custom hook for managing content manager data
 * @param listId
 */
const useContentManagerData = (listId: string | undefined) => {
    const [listDetails, setListDetails] = useState<CurationItem | null>(null);
    const [items, setItems] = useState<CurationItem[]>([]);
    const [parentListId, setParentListId] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [showSuccessIndicator, setShowSuccessIndicator] = useState<boolean>(false);
    const [deleteSuccessMessage, setDeleteSuccessMessage] = useState<string>("");
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [isPublishing, setIsPublishing] = useState<boolean>(false);
    const [showAddContentModal, setShowAddContentModal] = useState<boolean>(false);
    const [showEditContentModal, setShowEditContentModal] = useState<boolean>(false);
    const [isFetchingEditItem, setIsFetchingEditItem] = useState<boolean>(false);
    const [itemToEdit, setItemToEdit] = useState<CurationItem | null>(null);

    const EDIT_RESTRICTED_MESSAGE =
        "Asset editing is managed in Vimond CMS. Select a list, menu, or page to edit.";

    const fetchContent = useCallback(async () => {
        const idForFetching = listId;

        if (!idForFetching) {
            setError("No List ID provided in URL.");
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const listObject = await getListById(idForFetching);
            if (listObject) {
                const normalizedList = normalizeCurationItem(listObject);
                setListDetails(normalizedList);

                setItems(normalizedList.children ?? []);

                if (listObject.path?.length && listObject.path.length > 1) {
                    const parent = listObject.path[listObject.path.length - 2];
                    setParentListId(parent?.listId ?? null);
                } else {
                    setParentListId(null);
                }
            } else {
                setItems([]);
                setParentListId(null);
            }
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "An error occurred while fetching content.");
        } finally {
            setLoading(false);
        }
    }, [listId]);

    useEffect(() => {
        void fetchContent();
    }, [fetchContent]);

    const getParentToggleState = useCallback(() => {
        if (items.length > 0) {
            return items.every((child) => child.published);
        }
        return listDetails?.published ?? false;
    }, [items, listDetails]);

    const getSelectedItems = useCallback(
        (selectedItemIds: number[]) => items.filter((item) => selectedItemIds.includes(item.id)),
        [items],
    );

    const openAddContentModal = useCallback(() => setShowAddContentModal(true), []);
    const closeAddContentModal = useCallback(() => setShowAddContentModal(false), []);

    const openEditContentModal = useCallback(() => setShowEditContentModal(true), []);
    const closeEditContentModal = useCallback(() => setShowEditContentModal(false), []);

    const triggerSuccessIndicator = useCallback(() => {
        setShowSuccessIndicator(true);
        setTimeout(() => setShowSuccessIndicator(false), TIMING.SUCCESS_INDICATOR_DURATION_MS);
    }, []);

    const handleAddContent = useCallback(
        async (contentType: string, title: string, options: AddContentOptions) => {
            if (!listId) return;

            try {
                let newItem: CurationItem | null = null;
                const normalizedTitle = title.trim();
                const providedAlias = options.alias?.trim();
                const description = options.description?.trim();
                const companyId = options.companyId?.trim();
                const publisherId = options.publisherId?.trim();
                const themeBrandColor = options.themeBrandColor?.trim();
                const smartListTemplate = options.smartListTemplate?.trim();
                const smartListAdapterId = options.smartListAdapterId;

                if (contentType === "WEBSITE_STRUCTURE") {
                    const normalizedSlug = normalizedTitle
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")
                        .replace(/^-+|-+$/g, "");
                    const generatedAlias = normalizedSlug.length > 0 ? normalizedSlug : "list";
                    const aliasToUse =
                        providedAlias && providedAlias.length > 0 ? providedAlias : generatedAlias;
                    const logoUrl = options.logoUrl?.trim();
                    const result = await createWebsiteStructure({
                        parentId: listId,
                        title: normalizedTitle,
                        alias: aliasToUse,
                        description,
                        companyId,
                        publisherId,
                        themeBrandColor,
                        themeId: options.themeId,
                        logoUrl,
                    });
                    newItem = result.rootList ?? null;
                } else if (
                    contentType === "LIST" ||
                    contentType === "SMART_LIST" ||
                    contentType === "CAROUSEL"
                ) {
                    const aliasForList = providedAlias ?? "";
                    newItem = await createList(listId, contentType, normalizedTitle, aliasForList, {
                        description,
                        companyId,
                        publisherId,
                        themeBrandColor,
                        isSmartList: options.isSmartList,
                        smartListTemplate,
                        smartListAdapterId,
                        smartListGenre: options.smartListGenre,
                        smartListLabels: options.smartListLabels,
                        smartListSortOption: options.smartListSortOption,
                    });
                } else if (contentType === "MENU") {
                    const layoutPosition = options.layoutMenuPosition ?? "Header";
                    newItem = await createMenu(listId, normalizedTitle, layoutPosition);
                } else if (contentType === "PAGE") {
                    const normalizedPageSlug = normalizedTitle
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")
                        .replace(/^-+|-+$/g, "");
                    const trimmedPageSlug = options.pageSlug?.trim();
                    const pageSlug =
                        trimmedPageSlug && trimmedPageSlug.length > 0
                            ? trimmedPageSlug
                            : normalizedPageSlug;
                    newItem = await createPage(listId, normalizedTitle, pageSlug);
                } else if (contentType === "MENU_ITEM") {
                    const normalizedPosition = normalizedTitle
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")
                        .replace(/^-+|-+$/g, "");
                    const trimmedPosition = options.position?.trim();
                    const position =
                        trimmedPosition && trimmedPosition.length > 0
                            ? trimmedPosition
                            : normalizedPosition;
                    const imagePackId = options.imagePackId?.trim();
                    newItem = await createMenuItem(listId, normalizedTitle, position, {
                        imagePackId: imagePackId ?? undefined,
                    });
                } else if (contentType === "ASSET") {
                    const assetListId = options.assetListId?.trim();
                    const assetImagePackId = options.assetImagePackId?.trim();
                    if (!assetListId || !assetImagePackId) {
                        throw new Error("Asset requires listId and imagePackId");
                    }
                    newItem = await createAsset(
                        listId,
                        normalizedTitle,
                        assetListId,
                        assetImagePackId,
                        {
                            publisherId,
                        },
                    );
                }

                if (newItem) {
                    await fetchContent();
                    triggerSuccessIndicator();
                }
            } catch (e: unknown) {
                setError(
                    e instanceof Error ? e.message : "An error occurred while adding content.",
                );
            }
        },
        [fetchContent, listId, triggerSuccessIndicator],
    );

    const prepareItemForEdit = useCallback(
        async (selectedItemIds: number[]): Promise<CurationItem | null> => {
            if (selectedItemIds.length !== 1 || isFetchingEditItem) {
                return null;
            }

            if (!listId) {
                setError("Unable to determine the current list.");
                return null;
            }

            const selectedItemId = selectedItemIds[0];
            const currentSelection =
                items.find((child: CurationItem) => child.id === selectedItemId) ?? null;
            const isAssetSelection =
                (currentSelection?.contentType ?? "").toLowerCase() === "asset";

            if (isAssetSelection) {
                setError(EDIT_RESTRICTED_MESSAGE);
                return null;
            }

            try {
                setIsFetchingEditItem(true);
                const latestList = await getListById(listId);

                if (!latestList) {
                    setError("Unable to refresh list data from the API.");
                    return null;
                }

                const normalizedList = normalizeCurationItem(latestList);
                setListDetails(normalizedList);

                const refreshedItems = normalizedList.children ?? [];
                setItems(refreshedItems);

                const refreshedItem =
                    refreshedItems.find((child: CurationItem) => child.id === selectedItemId) ??
                    null;

                if (!refreshedItem) {
                    setError(
                        "Unable to locate the selected item in the refreshed data. Please try again.",
                    );
                    setItemToEdit(null);
                    closeEditContentModal();
                    return null;
                }

                const refreshedItemIsAsset =
                    (refreshedItem.contentType ?? "").toLowerCase() === "asset";

                if (refreshedItemIsAsset) {
                    setError(EDIT_RESTRICTED_MESSAGE);
                    setItemToEdit(null);
                    closeEditContentModal();
                    return null;
                }

                setItemToEdit(refreshedItem);
                openEditContentModal();
                return refreshedItem;
            } catch (e: unknown) {
                setError(
                    (e instanceof Error ? e.message : null) ??
                        "Failed to load the selected item for editing.",
                );
                return null;
            } finally {
                setIsFetchingEditItem(false);
            }
        },
        [
            closeEditContentModal,
            EDIT_RESTRICTED_MESSAGE,
            isFetchingEditItem,
            items,
            listId,
            openEditContentModal,
        ],
    );

    const handleEditContent = useCallback(
        async (title: string, options: EditContentOptions) => {
            if (!itemToEdit || !listId) return;

            try {
                await updateListItem(itemToEdit, title, options);
                const listObject = await getListById(listId);

                if (listObject) {
                    const normalizedList = normalizeCurationItem(listObject);
                    setListDetails(normalizedList);

                    const refreshedItems = normalizedList.children ?? [];
                    setItems(refreshedItems);

                    const refreshedItem =
                        refreshedItems.find((child: CurationItem) => child.id === itemToEdit.id) ??
                        null;

                    setItemToEdit(refreshedItem);
                } else {
                    setItems([]);
                    setItemToEdit(null);
                }

                triggerSuccessIndicator();
            } catch (e: unknown) {
                setError(
                    e instanceof Error ? e.message : "An error occurred while editing content.",
                );
            }
        },
        [itemToEdit, listId, triggerSuccessIndicator],
    );

    const deleteItems = useCallback(
        async (selectedItemIds: number[]): Promise<boolean> => {
            if (selectedItemIds.length === 0) {
                return false;
            }

            try {
                setIsDeleting(true);
                const deletePromises = selectedItemIds.map((id) => deleteListItem(id));
                await Promise.all(deletePromises);
                await fetchContent();
                setDeleteSuccessMessage("Delete successful");
                setTimeout(() => setDeleteSuccessMessage(""), TIMING.SUCCESS_INDICATOR_DURATION_MS);
                return true;
            } catch (e: unknown) {
                setError(
                    e instanceof Error ? e.message : "An error occurred while deleting items.",
                );
                return false;
            } finally {
                setIsDeleting(false);
            }
        },
        [fetchContent],
    );

    const togglePublishStatus = useCallback(
        async (selectedItemIds: number[]) => {
            if (!listDetails) return;

            const unpublishItem = async (id: number): Promise<void> => {
                try {
                    await setPublishStatus(id, false);
                } catch (error: unknown) {
                    throw error;
                }
            };

            try {
                setIsPublishing(true);
                if (selectedItemIds.length > 0) {
                    const selectedItems = getSelectedItems(selectedItemIds);
                    if (selectedItems.length === 0) return;

                    const areSelectedPublished = selectedItems.every((item) => item.published);
                    const newPublishStatus = !areSelectedPublished;

                    if (newPublishStatus) {
                        await setPublishStatusForItems(selectedItemIds, newPublishStatus);
                    } else {
                        for (const item of selectedItems) {
                            if (item.published) {
                                await unpublishItem(item.id);
                            }
                        }
                    }
                    await fetchContent();
                    triggerSuccessIndicator();
                } else {
                    if (listId === "editorial_root" || listDetails.listId === "editorial_root") {
                        setError(
                            "Publishing the entire editorial root is disabled. Select specific items instead.",
                        );
                        return;
                    }

                    const currentParentPublished = getParentToggleState();
                    const newPublishStatus = !currentParentPublished;

                    if (newPublishStatus) {
                        await setPublishStatus(listDetails.id, newPublishStatus);
                    } else {
                        if (listDetails.published) {
                            await unpublishItem(listDetails.id);
                        }
                    }
                    await fetchContent();
                    triggerSuccessIndicator();
                }
            } catch (e: unknown) {
                setError(
                    e instanceof Error
                        ? e.message
                        : "An error occurred while toggling publish status.",
                );
            } finally {
                setIsPublishing(false);
            }
        },
        [
            getParentToggleState,
            getSelectedItems,
            listDetails,
            listId,
            triggerSuccessIndicator,
            fetchContent,
        ],
    );

    return {
        listDetails,
        items,
        parentListId,
        loading,
        error,
        showSuccessIndicator,
        deleteSuccessMessage,
        showAddContentModal,
        openAddContentModal,
        closeAddContentModal,
        showEditContentModal,
        openEditContentModal,
        closeEditContentModal,
        isFetchingEditItem,
        itemToEdit,
        setItemToEdit,
        setError,
        fetchContent,
        handleAddContent,
        prepareItemForEdit,
        handleEditContent,
        deleteItems,
        isDeleting,
        isPublishing,
        togglePublishStatus,
        getParentToggleState,
        getSelectedItems,
        triggerSuccessIndicator,
    };
};

export default useContentManagerData;
